import html2canvas from "html2canvas";

import type { FeedbackScreenshot } from "../api/feedback";

type FeedbackEventInput = {
  type: string;
  label?: string;
  path?: string;
  message?: string;
  metadata?: Record<string, unknown>;
};

export interface FeedbackClientEvent extends FeedbackEventInput {
  at: string;
}

export interface FeedbackBundle {
  page: Record<string, unknown>;
  client: Record<string, unknown>;
  events: FeedbackClientEvent[];
  screenshot?: FeedbackScreenshot | null;
}

const MAX_EVENTS = 40;
const SECRET_QUERY_KEYS = new Set([
  "access_token",
  "api_key",
  "code",
  "invite",
  "password",
  "refresh_token",
  "session_state",
  "state",
  "token",
]);

const events: FeedbackClientEvent[] = [];
let instrumentationInstalled = false;

function safeText(value: unknown, maxLength = 180): string | undefined {
  if (value === null || value === undefined) return undefined;
  const text = String(value).replace(/\s+/g, " ").trim();
  return text ? text.slice(0, maxLength) : undefined;
}

function sanitizeUrl(value: string): string {
  try {
    const url = new URL(value, window.location.origin);
    SECRET_QUERY_KEYS.forEach((key) => url.searchParams.delete(key));
    return url.toString();
  } catch {
    return value.split("?")[0] ?? value;
  }
}

function sanitizePath(pathname: string, search: string): string {
  const params = new URLSearchParams(search);
  SECRET_QUERY_KEYS.forEach((key) => params.delete(key));
  const query = params.toString();
  return `${pathname}${query ? `?${query}` : ""}`;
}

function describeElement(target: EventTarget | null): string | undefined {
  if (!(target instanceof Element)) return undefined;
  const interactive = target.closest("button,a,[role='button'],[aria-label],input,textarea,select");
  if (!interactive) return undefined;
  const element = interactive as HTMLElement;
  const explicit =
    element.getAttribute("aria-label") ||
    element.getAttribute("title") ||
    element.getAttribute("placeholder") ||
    element.innerText ||
    element.textContent ||
    element.tagName.toLowerCase();
  return safeText(explicit, 120);
}

export function recordFeedbackEvent(event: FeedbackEventInput): void {
  events.push({
    ...event,
    label: safeText(event.label, 160),
    path: event.path ? sanitizeUrl(event.path) : undefined,
    message: safeText(event.message, 240),
    at: new Date().toISOString(),
  });
  if (events.length > MAX_EVENTS) {
    events.splice(0, events.length - MAX_EVENTS);
  }
}

export function getFeedbackEvents(): FeedbackClientEvent[] {
  return events.slice(-MAX_EVENTS);
}

export function installFeedbackInstrumentation(): () => void {
  if (instrumentationInstalled || typeof window === "undefined") {
    return () => undefined;
  }
  instrumentationInstalled = true;

  const onClick = (event: MouseEvent) => {
    const element = event.target instanceof Element ? event.target : null;
    if (element?.closest("[data-feedback-exclude='true']")) return;
    const label = describeElement(event.target);
    if (label) recordFeedbackEvent({ type: "click", label });
  };

  const onError = (event: ErrorEvent) => {
    recordFeedbackEvent({
      type: "console_error",
      message: event.message,
      metadata: { source: safeText(event.filename, 180), line: event.lineno },
    });
  };

  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    recordFeedbackEvent({
      type: "unhandled_rejection",
      message: event.reason instanceof Error ? event.reason.message : String(event.reason),
    });
  };

  document.addEventListener("click", onClick, { capture: true });
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onUnhandledRejection);

  return () => {
    document.removeEventListener("click", onClick, { capture: true });
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onUnhandledRejection);
    instrumentationInstalled = false;
  };
}

export function recordFeedbackRoute(path: string, title = document.title): void {
  recordFeedbackEvent({ type: "route", path, label: title });
}

async function captureScreenshot(): Promise<FeedbackScreenshot | null> {
  if (typeof document === "undefined" || typeof window === "undefined") return null;
  try {
    const canvas = await html2canvas(document.body, {
      allowTaint: false,
      backgroundColor: window.getComputedStyle(document.body).backgroundColor || "#ffffff",
      height: window.innerHeight,
      ignoreElements: (element) => element.closest("[data-feedback-exclude='true']") !== null,
      logging: false,
      scale: Math.min(window.devicePixelRatio || 1, 1.5),
      scrollX: -window.scrollX,
      scrollY: -window.scrollY,
      useCORS: true,
      width: window.innerWidth,
      windowHeight: document.documentElement.scrollHeight,
      windowWidth: document.documentElement.scrollWidth,
      x: window.scrollX,
      y: window.scrollY,
    });

    const maxWidth = 1280;
    const ratio = canvas.width > maxWidth ? maxWidth / canvas.width : 1;
    const output = document.createElement("canvas");
    output.width = Math.max(1, Math.round(canvas.width * ratio));
    output.height = Math.max(1, Math.round(canvas.height * ratio));
    const ctx = output.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(canvas, 0, 0, output.width, output.height);
    let dataUrl = output.toDataURL("image/jpeg", 0.72);
    if (dataUrl.length > 2_000_000) {
      dataUrl = output.toDataURL("image/jpeg", 0.48);
    }
    return {
      data_url: dataUrl,
      content_type: "image/jpeg",
      width: output.width,
      height: output.height,
    };
  } catch (error) {
    recordFeedbackEvent({
      type: "screenshot_error",
      message: error instanceof Error ? error.message : String(error),
    });
    return null;
  }
}

export async function createFeedbackBundle({
  includeScreenshot,
}: {
  includeScreenshot: boolean;
}): Promise<FeedbackBundle> {
  const url = typeof window === "undefined" ? "" : sanitizeUrl(window.location.href);
  const screenshot = includeScreenshot ? await captureScreenshot() : null;
  const nav = typeof navigator === "undefined" ? undefined : navigator;
  return {
    page: {
      url,
      path: typeof window === "undefined" ? "" : sanitizePath(window.location.pathname, window.location.search),
      title: typeof document === "undefined" ? "" : document.title,
      referrer: typeof document === "undefined" ? "" : sanitizeUrl(document.referrer),
      captured_at: new Date().toISOString(),
    },
    client: {
      user_agent: nav?.userAgent,
      language: nav?.language,
      online: nav?.onLine,
      viewport_width: typeof window === "undefined" ? undefined : window.innerWidth,
      viewport_height: typeof window === "undefined" ? undefined : window.innerHeight,
      screen_width: typeof window === "undefined" ? undefined : window.screen?.width,
      screen_height: typeof window === "undefined" ? undefined : window.screen?.height,
      device_pixel_ratio: typeof window === "undefined" ? undefined : window.devicePixelRatio,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    events: getFeedbackEvents(),
    screenshot,
  };
}
